// A small, robust image cache that:
// - Caches object URLs per fully resolved image URL
// - Deduplicates concurrent fetches
// - Provides prefetch and getObjectUrl utilities
// - Cleans up object URLs when explicitly cleared (not auto to avoid breaking references)

type CacheEntry = {
  objectUrl?: string;
  promise?: Promise<string>;
};

const imageCache: Map<string, CacheEntry> = new Map();

async function fetchAsObjectUrl(url: string): Promise<string> {
  const response = await fetch(url, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Image fetch failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  return objectUrl;
}

export function getCachedObjectUrl(url: string): string | undefined {
  const entry = imageCache.get(url);
  return entry?.objectUrl;
}

export function prefetchImage(url: string): Promise<string> {
  if (!url) return Promise.reject(new Error("prefetchImage: empty url"));

  const existing = imageCache.get(url);
  if (existing?.objectUrl) {
    return Promise.resolve(existing.objectUrl);
  }
  if (existing?.promise) {
    return existing.promise;
  }

  const promise = fetchAsObjectUrl(url)
    .then((objectUrl) => {
      imageCache.set(url, { objectUrl });
      return objectUrl;
    })
    .catch((err) => {
      const entry = imageCache.get(url);
      if (entry && entry.promise) {
        imageCache.delete(url);
      }
      throw err;
    });

  imageCache.set(url, { promise });
  return promise;
}

export async function getOrFetchObjectUrl(url: string): Promise<string> {
  const cached = getCachedObjectUrl(url);
  if (cached) return cached;
  return prefetchImage(url);
}

export function clearImageFromCache(url: string): void {
  const entry = imageCache.get(url);
  if (entry?.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  imageCache.delete(url);
}

export function clearAllImagesFromCache(): void {
  imageCache.forEach((entry) => {
    if (entry.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl);
    }
  });
  imageCache.clear();
}
